import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@/components/Icon";
import { Screen, Header } from "@/components/Layout";
import { Spinner } from "@/components/Controls";
import { colors, spacing, radius, fonts, type } from "@/theme";
import { getStoredUserId, fetchHistory, fetchUser, fetchStats } from "@/api";

function dayLabel(iso) {
  const d = new Date(iso);
  const now = new Date();
  const days = Math.floor((new Date(now.getFullYear(), now.getMonth(), now.getDate()) - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
}

function groupByDay(messages) {
  const groups = [];
  for (const m of messages) {
    const day = dayLabel(m.created_at);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.items.push(m);
    else groups.push({ day, items: [m] });
  }
  return groups;
}

export default function History() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [memories, setMemories] = useState([]);
  const [stats, setStats] = useState(null);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const id = getStoredUserId();
      if (!id) {
        navigate("/onboarding", { replace: true });
        return;
      }
      const [msgs, profile, s] = await Promise.all([fetchHistory(id), fetchUser(id).catch(() => null), fetchStats(id)]);
      setGroups(groupByDay(msgs));
      if (profile) {
        setMemories(profile.memories);
        setName(profile.user.first_name);
      }
      setStats(s);
      setLoading(false);
    })();
  }, [navigate]);

  return (
    <Screen testID="history-screen">
      <Header title="Looking back" testID="history-back" />
      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: spacing["3xl"] }}><Spinner size={40} /></div>
      ) : (
        <div style={{ padding: `${spacing.xl}px ${spacing.lg}px ${spacing["2xl"]}px` }}>
          {stats && stats.total > 0 && (
            <div data-testid="weekly-summary" style={{ background: colors.brandPrimary, borderRadius: radius.lg, padding: spacing.xl, marginBottom: spacing.xl }}>
              <div style={{ ...fonts.displaySemiBold, fontSize: type.xl, color: colors.onBrandPrimary, lineHeight: 1.3, marginBottom: spacing.lg }}>
                {stats.this_week > 0 ? `We talked ${stats.this_week} ${stats.this_week === 1 ? "time" : "times"} this week${name ? `, ${name}` : ""}.` : `Welcome back${name ? `, ${name}` : ""}. It's lovely to see you.`}
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <div style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ ...fonts.displayBold, fontSize: type["2xl"], color: colors.onBrandPrimary }}>{stats.streak}</div>
                  <div style={{ ...fonts.textSemiBold, fontSize: type.base, color: colors.brandTertiary }}>day streak</div>
                </div>
                <div style={{ width: 1, height: 40, background: colors.brandSecondary }} />
                <div style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ ...fonts.displayBold, fontSize: type["2xl"], color: colors.onBrandPrimary }}>{stats.total}</div>
                  <div style={{ ...fonts.textSemiBold, fontSize: type.base, color: colors.brandTertiary }}>days together</div>
                </div>
              </div>
            </div>
          )}

          {memories.length > 0 && (
            <div style={{ background: colors.surfaceSecondary, borderRadius: radius.lg, padding: spacing.lg, marginBottom: spacing.xl }}>
              <div style={{ ...fonts.textBold, fontSize: type.lg, color: colors.onSurface, marginBottom: spacing.md }}>What I remember about you</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: spacing.sm }}>
                {memories.slice(0, 10).map((m) => (
                  <div key={m.id} data-testid={`history-memory-${m.id}`} style={{ display: "flex", alignItems: "center", gap: spacing.xs, background: colors.brandTertiary, borderRadius: radius.pill, padding: `${spacing.sm}px ${spacing.md}px` }}>
                    <Icon name="bookmark" size={16} color={colors.brand} />
                    <span style={{ ...fonts.textBold, fontSize: type.base, color: colors.onBrandTertiary }}>{m.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {groups.length === 0 ? (
            <div data-testid="history-empty" style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: spacing["3xl"], gap: spacing.lg }}>
              <Icon name="message-circle" size={48} color={colors.border} />
              <div style={{ ...fonts.textSemiBold, fontSize: type.lg, color: colors.muted, textAlign: "center", padding: `0 ${spacing.xl}px`, lineHeight: 1.5 }}>
                Your conversations will appear here after your first chat.
              </div>
            </div>
          ) : (
            groups.map((g) => (
              <div key={g.day} style={{ marginBottom: spacing.xl, display: "flex", flexDirection: "column", gap: spacing.md }}>
                <div style={{ ...fonts.displaySemiBold, fontSize: type.lg, color: colors.brand }}>{g.day}</div>
                {g.items.map((m, i) => (
                  <div key={`${g.day}-${i}`} data-testid={`history-bubble-${m.role}`} style={{
                    maxWidth: "88%", borderRadius: radius.lg, padding: `${spacing.md}px ${spacing.lg}px`,
                    ...(m.role === "assistant"
                      ? { background: colors.surfaceSecondary, alignSelf: "flex-start", borderTopLeftRadius: radius.sm }
                      : { background: colors.brandPrimary, alignSelf: "flex-end", borderTopRightRadius: radius.sm }),
                  }}>
                    <span style={{ ...fonts.textSemiBold, fontSize: type.lg, lineHeight: 1.4, color: m.role === "assistant" ? colors.onSurface : colors.onBrandPrimary }}>{m.text}</span>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </Screen>
  );
}
